<div class="audit-timeline">

  <!-- Back link -->
  <button type="button" class="back-link" (click)="goBack()">
    ‹ Back to Audit Log
  </button>

  <!-- Entity header card -->
  <div class="entity-head" *ngIf="entityType && entityId && !loading && !error && entries.length > 0">
    <span class="entity-tag">{{ entityType | titlecase }}</span>
    <div class="entity-info">
      <h1 class="entity-label">{{ entries[0].summary | slice:0:60 }}</h1>
      <div class="entity-id">{{ entityId }}</div>
    </div>
    <div class="event-count">
      <b>{{ entries.length }}</b>
      <span>events</span>
    </div>
  </div>

  <!-- Loading spinner -->
  <div class="spinner-container" *ngIf="loading">
    <mat-spinner diameter="40"></mat-spinner>
  </div>

  <!-- Error state -->
  <div class="state-card" *ngIf="!loading && error">
    <mat-icon class="state-icon state-icon--error">error_outline</mat-icon>
    <p>Failed to load timeline. Please try again.</p>
    <button mat-button (click)="goBack()" class="state-back-btn">Back to Audit Log</button>
  </div>

  <!-- Empty state -->
  <div class="state-card" *ngIf="!loading && !error && entries.length === 0">
    <mat-icon class="state-icon">history</mat-icon>
    <p>No history found for this record.</p>
    <button mat-button (click)="goBack()" class="state-back-btn">Back to Audit Log</button>
  </div>

  <!-- Timeline -->
  <div class="timeline" *ngIf="!loading && !error && entries.length > 0">

    <div
      class="timeline-event"
      *ngFor="let entry of entries"
      [id]="'event-' + entry.commitId"
      [class.highlighted]="entry.commitId === highlightedCommitId">
      <!-- Vertical rail node -->
      <span class="tl-node" [ngClass]="nodeClass(entry.action)"></span>

      <!-- Event card (styled mat-expansion-panel) -->
      <mat-expansion-panel
        class="event-panel"
        [disabled]="!hasChanges(entry)"
        [expanded]="entry.commitId === highlightedCommitId && hasChanges(entry)">

        <mat-expansion-panel-header class="event-header">
          <mat-panel-title class="event-title">
            <span class="event-when">{{ entry.timestamp | date:'medium' }}</span>
            <span class="who" [class.who--svc]="isServiceAccount(entry.user)">
              <span class="who-ini">{{ initials(entry.user) }}</span>
              <span class="who-name">{{ entry.user }}</span>
            </span>
            <mat-chip-set class="event-chip-set">
              <mat-chip [ngClass]="chipClass(entry.action)" disableRipple>
                {{ entry.action | titlecase }}
              </mat-chip>
            </mat-chip-set>
            <span class="event-summary">{{ entry.summary }}</span>
          </mat-panel-title>
        </mat-expansion-panel-header>

        <!-- Changes detail -->
        <div class="changes-detail" *ngIf="hasChanges(entry)">
          <div class="change-row" *ngFor="let change of entry.changes">
            <span class="change-field">{{ change.field }}</span>
            <span class="change-vals">
              <!-- Old value -->
              <ng-container *ngIf="change.oldValue !== null; else emptyOld">
                <span [ngClass]="valueClass(change.oldValue, 'old', change.newValue)">
                  <span *ngIf="isEnum(change.oldValue)" class="val-pill">{{ formatValue(change.oldValue) }}</span>
                  <ng-container *ngIf="!isEnum(change.oldValue)">{{ formatValue(change.oldValue) }}</ng-container>
                </span>
              </ng-container>
              <ng-template #emptyOld>
                <span class="val-empty">—</span>
              </ng-template>

              <span class="change-arrow">→</span>

              <!-- New value -->
              <ng-container *ngIf="change.newValue !== null; else emptyNew">
                <span [ngClass]="valueClass(change.newValue, 'new', change.oldValue)">
                  <span *ngIf="isEnum(change.newValue)" class="val-pill">{{ formatValue(change.newValue) }}</span>
                  <ng-container *ngIf="!isEnum(change.newValue)">{{ formatValue(change.newValue) }}</ng-container>
                </span>
              </ng-container>
              <ng-template #emptyNew>
                <span class="val-empty">—</span>
              </ng-template>
            </span>
          </div>
        </div>

      </mat-expansion-panel>
    </div>

  </div>

  <!-- Related records panel -->
  <div class="related-panel" *ngIf="showRelatedPanel && !loading && !error">
    <h2 class="related-title">Related records</h2>

    <div class="related-loading" *ngIf="relatedLoading">
      <mat-spinner diameter="20"></mat-spinner>
    </div>

    <ng-container *ngIf="!relatedLoading">
      <ng-container *ngIf="relatedTree && relatedTree.nodes.length > 0; else noRelated">
        <div class="related-tree">
          <ng-container
            *ngFor="let node of relatedTree.nodes"
            [ngTemplateOutlet]="relatedNode"
            [ngTemplateOutletContext]="{ node: node, depth: 0 }">
          </ng-container>
        </div>

        <p class="related-truncated" *ngIf="relatedTree.truncated">
          Some related records are not shown (truncated).
        </p>
      </ng-container>

      <ng-template #noRelated>
        <p class="related-empty">No related records.</p>
      </ng-template>
    </ng-container>

  </div>

  <!-- Recursive related-record row: renders a node, then (if expanded) its inline details -->
  <ng-template #relatedNode let-node="node" let-depth="depth">
    <div class="related-row-wrap">
      <div class="related-row" [style.paddingLeft.px]="16 + depth * 22">

        <!-- Expand/collapse chevron — every node expands now; sibling control, not nested in the navigate button -->
        <button
          type="button"
          class="related-chevron"
          [class.related-chevron--open]="isExpanded(node)"
          [attr.aria-label]="isExpanded(node) ? 'Collapse' : 'Expand'"
          [attr.aria-expanded]="isExpanded(node)"
          (click)="toggleNode(node)">
          <mat-icon>chevron_right</mat-icon>
        </button>

        <button type="button" class="related-open" (click)="openRecord(node)">
          <span class="related-type-tag">{{ node.entityType | titlecase }}</span>

          <span class="related-label">{{ node.label }}</span>

          <span class="related-business-id" *ngIf="node.businessId">{{ node.businessId }}</span>

          <span class="related-status" *ngIf="node.status">{{ node.status | titlecase }}</span>

          <span class="related-deleted-tag" *ngIf="node.deleted">Deleted</span>
        </button>
      </div>

      <!-- Inline details: lazy-loaded change history, then (if any) nested records -->
      <div class="related-details" *ngIf="isExpanded(node)">

        <!-- History subsection (depth-aligned one level in from this row) -->
        <div class="related-history" [style.paddingLeft.px]="16 + (depth + 1) * 22">
          <div class="related-subhead">History</div>

          <div class="related-history-state" *ngIf="isHistoryLoading(node)">
            <mat-spinner diameter="16"></mat-spinner>
            <span>Loading…</span>
          </div>

          <p class="related-history-state related-history-state--error" *ngIf="!isHistoryLoading(node) && isHistoryError(node)">
            Couldn't load history
          </p>

          <p
            class="related-history-state"
            *ngIf="!isHistoryLoading(node) && !isHistoryError(node) && historyEntries(node).length === 0">
            No changes recorded
          </p>

          <div
            class="related-history-list"
            *ngIf="!isHistoryLoading(node) && !isHistoryError(node) && historyEntries(node).length > 0">
            <div class="related-history-row" *ngFor="let entry of historyEntries(node)">
              <span class="related-history-when">{{ entry.timestamp | date:'medium' }}</span>
              <span class="related-history-chip" [ngClass]="chipClass(entry.action)">{{ entry.action | titlecase }}</span>
              <span class="related-history-user">{{ entry.user }}</span>
              <span class="related-history-summary">{{ entry.summary }}</span>
            </div>
          </div>
        </div>

        <!-- Open full timeline — right under this node's History, above its nested records -->
        <button
          type="button"
          class="related-full-link"
          [style.marginLeft.px]="16 + (depth + 1) * 22"
          (click)="openRecord(node)">
          open full timeline →
        </button>

        <!-- Records subsection (nested children — each child row computes its own depth indentation) -->
        <div class="related-records" *ngIf="node.children && node.children.length > 0">
          <div class="related-subhead" [style.paddingLeft.px]="16 + (depth + 1) * 22">Records</div>
          <div class="related-children">
            <ng-container
              *ngFor="let child of node.children"
              [ngTemplateOutlet]="relatedNode"
              [ngTemplateOutletContext]="{ node: child, depth: depth + 1 }">
            </ng-container>
          </div>
        </div>
      </div>
    </div>
  </ng-template>

</div>
